import { useEffect, useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Paperclip, Upload, Trash2, Download, Loader2, FileText } from "lucide-react";
import { toast } from "sonner";

/**
 * Inline attachments widget. Shows existing files, allows upload + delete.
 * Used on every record's detail page (invoice, bill, journal, expense, ...).
 */
export default function Attachments({ entityType, entityId, compact = false }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const inputRef = useRef(null);

    const refresh = async () => {
        if (!entityId) return;
        setLoading(true);
        try {
            const { data } = await api.get("/attachments", { params: { entity_type: entityType, entity_id: entityId } });
            setItems(data);
        } finally { setLoading(false); }
    };
    useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [entityType, entityId]);

    const onFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 10 * 1024 * 1024) {
            toast.error("File must be under 10 MB");
            return;
        }
        const fd = new FormData();
        fd.append("entity_type", entityType);
        fd.append("entity_id", entityId);
        fd.append("file", file);
        setUploading(true);
        try {
            const token = localStorage.getItem("vyastra_token");
            const res = await fetch(`${API}/attachments`, {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` },
                body: fd,
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.detail || "Upload failed");
            }
            toast.success(`Uploaded ${file.name}`);
            await refresh();
        } catch (e) {
            toast.error(e.message);
        } finally {
            setUploading(false);
            if (inputRef.current) inputRef.current.value = "";
        }
    };

    const downloadFile = async (a) => {
        const token = localStorage.getItem("vyastra_token");
        const res = await fetch(`${API}/attachments/${a.id}/download`, { headers: { Authorization: `Bearer ${token}` } });
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url; link.download = a.filename;
        document.body.appendChild(link); link.click(); link.remove();
        URL.revokeObjectURL(url);
    };

    const remove = async (a) => {
        if (!confirm(`Delete "${a.filename}"?`)) return;
        await api.delete(`/attachments/${a.id}`);
        toast.success("Attachment deleted");
        await refresh();
    };

    return (
        <div className={compact ? "" : "bg-card border border-border rounded-md p-4"} data-testid="attachments-panel">
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    <Paperclip className="w-3.5 h-3.5 text-muted-foreground" />
                    <span className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-semibold">
                        Attachments {items.length > 0 && <span className="ml-1 px-1.5 py-0.5 rounded bg-muted text-[10px]">{items.length}</span>}
                    </span>
                </div>
                <Button variant="outline" size="sm" onClick={() => inputRef.current?.click()} disabled={uploading} data-testid="attach-upload">
                    {uploading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1.5" />}
                    {uploading ? "Uploading…" : "Upload"}
                </Button>
                <input ref={inputRef} type="file" className="hidden" onChange={onFile} accept=".pdf,.png,.jpg,.jpeg,.webp,.gif,.doc,.docx,.xls,.xlsx,.csv,.txt" data-testid="attach-input" />
            </div>
            {loading ? (
                <div className="text-muted-foreground text-[12px] py-2">Loading…</div>
            ) : items.length === 0 ? (
                <div className="text-muted-foreground text-[12px] py-2">No attachments yet. Upload receipts, signed contracts, supporting docs (max 10 MB each).</div>
            ) : (
                <ul className="divide-y divide-border">
                    {items.map((a) => (
                        <li key={a.id} className="flex items-center justify-between gap-2 py-2 text-[12.5px]" data-testid={`attach-item-${a.id}`}>
                            <div className="flex items-center gap-2 min-w-0">
                                <FileText className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                                <div className="truncate">
                                    <div className="truncate font-medium">{a.filename}</div>
                                    <div className="text-[10px] text-muted-foreground">{(a.size / 1024).toFixed(1)} KB · {a.uploaded_by_name || "—"} · {a.uploaded_at?.slice(0, 10)}</div>
                                </div>
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0">
                                <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => downloadFile(a)} data-testid={`attach-download-${a.id}`}><Download className="w-3.5 h-3.5" /></Button>
                                <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive" onClick={() => remove(a)} data-testid={`attach-delete-${a.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
